"use client";

import { useCallback, useEffect, useMemo } from "react";
import {
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { ProjectCard } from "@/components/features/projects/ProjectCard";
import { ProjectCardsSkeletonGrid } from "@/components/features/projects/ProjectCardSkeleton";
import { ReorderableProjectGrid } from "@/components/features/projects/ReorderableProjectGrid";
import { Badge } from "@/components/ui";
import { Button } from "@/components/ui/primitives/button";
import { Input } from "@/components/ui/primitives/input";
import { ProgressiveBlur } from "@/components/ui/primitives/progressive-blur";
import {
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@/components/ui/tabs-simple";
import { FolderTree, Plus } from "lucide-react";
import { suppressNextDocumentClick } from "@/lib/manualIdOrder";
import { applyProjectManualOrder } from "../projectManualOrder";
import type { Project } from "../types/Project";

export type ProjectsPageViewProps = {
  projects: Project[];
  isLoading?: boolean;
  onCreateProject: () => void;
  onProjectClick: (projectId: string) => void;
  onHowItWorksClick: () => void;
  isAdmin?: boolean;
  adminProjects?: Project[];
  isAdminLoading?: boolean;
  adminError?: string | null;
  adminSearchValue?: string;
  onAdminSearchChange?: (value: string) => void;
  /** Saved global project ID order for the current user. */
  orderedProjectIds?: string[];
  /** Merge freshly loaded project IDs into the saved order. */
  onSyncProjectOrder?: (projectIds: string[]) => void;
  /** Persist a drag reorder within the currently visible list. */
  onReorderProjects?: (
    activeId: string,
    overId: string,
    visibleIds: string[],
  ) => void;
  /** When false, render plain cards without drag handles. */
  isReorderEnabled?: boolean;
};

export function ProjectsPageView({
  projects,
  isLoading = false,
  onCreateProject,
  isAdmin = false,
  adminProjects = [],
  isAdminLoading = false,
  adminError = null,
  adminSearchValue = "",
  onAdminSearchChange,
  orderedProjectIds = [],
  onSyncProjectOrder,
  onReorderProjects,
  isReorderEnabled = false,
}: ProjectsPageViewProps) {
  const orderedProjects = useMemo(
    () => applyProjectManualOrder(projects, orderedProjectIds),
    [projects, orderedProjectIds],
  );

  useEffect(() => {
    if (!isReorderEnabled || projects.length === 0) return;
    onSyncProjectOrder?.(projects.map((project) => project.id));
  }, [isReorderEnabled, onSyncProjectOrder, projects]);

  const allProjects = orderedProjects;
  const newProjects = orderedProjects.filter((p) => p.status === "new");
  const openedProjects = orderedProjects.filter((p) => p.status === "opened");
  const closedProjects = orderedProjects.filter((p) => p.status === "closed");

  // Filter admin projects by search value (email or title)
  const filteredAdminProjects = adminProjects.filter((p) => {
    if (!adminSearchValue.trim()) return true;
    const searchLower = adminSearchValue.toLowerCase();
    const matchesEmail = p.ownerEmail?.toLowerCase().includes(searchLower) ?? false;
    const matchesTitle = p.title.toLowerCase().includes(searchLower);
    return matchesEmail || matchesTitle;
  });

  const adminAllProjects = filteredAdminProjects;
  const adminNewProjects = filteredAdminProjects.filter((p) => p.status === "new");
  const adminOpenedProjects = filteredAdminProjects.filter((p) => p.status === "opened");
  const adminClosedProjects = filteredAdminProjects.filter((p) => p.status === "closed");

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent, visibleIds: string[]) => {
      // Always swallow the post-drag click, even if order did not change.
      suppressNextDocumentClick();

      const { active, over } = event;
      if (!over || active.id === over.id) return;
      onReorderProjects?.(String(active.id), String(over.id), visibleIds);
    },
    [onReorderProjects],
  );

  const handleDragCancel = useCallback(() => {
    suppressNextDocumentClick();
  }, []);

  const renderProjectGrid = (
    projectList: Project[],
    options?: { loadingOverride?: boolean; enableReorder?: boolean },
  ) => {
    const loadingFlag = options?.loadingOverride ?? isLoading;
    const enableReorder = Boolean(
      options?.enableReorder && isReorderEnabled && onReorderProjects,
    );

    if (loadingFlag) {
      return <ProjectCardsSkeletonGrid />;
    }

    if (projectList.length === 0) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <div className="space-y-2">
              <p className="text-muted-foreground/80 text-md font-semibold" translate="yes">
                Create Your First Project
              </p>
              <p className="text-muted-foreground/60 text-sm max-w-md leading-relaxed" translate="yes">
                Create project to organize interviews and generate insights{" "}
                <br />
                that will help you understand your customers better
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={onCreateProject}
              className="bg-secondary text-[var(--text-secondary)] hover:text-[var(--text)] !pr-4.5 rounded-full"
            >
              <Plus className="w-4 h-4 -mr-1" />
              <span translate="yes">Create Project</span>
            </Button>
          </div>
        </div>
      );
    }

    if (!enableReorder) {
      return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {projectList.map((project) => (
            <ProjectCard
              key={project.id}
              title={project.title}
              status={project.status}
              recordsCount={project.recordsCount}
              projectReportsCount={project.projectReportsCount}
              activeInterviewRoomsCount={project.activeInterviewRoomsCount ?? 0}
              projectId={project.id}
              ownerEmail={project.ownerEmail}
            />
          ))}
        </div>
      );
    }

    return (
      <ReorderableProjectGrid
        projects={projectList}
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      />
    );
  };

  return (
    <div data-webmcp-projects={projects.map((project) => project.id).join(",")} className="relative w-full h-[calc(100vh-78px)]">
      <div
        className="overflow-y-auto flex-1 min-w-0 w-full h-full p-0 relative [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-black [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:hover:w-2"
        style={{
          backgroundColor: "var(--background-custom)",
          scrollbarWidth: "thin",
          scrollbarColor: "black transparent",
        }}
      >
        <ProgressiveBlur
          direction="top"
          blurLayers={8}
          blurIntensity={2}
          className="sticky top-0 z-10 h-16"
        />
        <div className="flex flex-col mt-0 w-full">
          <main className="flex-1 px-4 pb-4 pt-0 w-full h-full">
            <div className="w-full max-w-[996px] mx-auto space-y-6">
              {/* Header */}
              <div className="space-y-4">
                {/* First row: Icon + How it works button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-center w-17 h-17 rounded-2xl bg-[var(--secondary)] ">
                    <FolderTree className="w-6 h-6 text-[var(--text-secondary)]" />
                  </div>

                  {/* <Button
                    variant="ghost"
                    size="default"
                    onClick={onHowItWorksClick}
                    className="text-[var(--text-secondary)]/50 hover:text-[var(--text)]/50 text-sm"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    How it works
                  </Button> */}
                </div>

                {/* Second row: Title + Create Project button + Description */}
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <h1 className="text-3xl font-semibold text-[var(--text)]" translate="yes">
                      Projects
                    </h1>

                    <Button
                      onClick={onCreateProject}
                      className="rounded-lg bg-[var(--brand)]/15 text-[var(--brand)] hover:bg-[var(--brand)]/90 hover:text-white cursor-pointer"
                      style={{
                        transition: "background-color 250ms ease-in-out, color 300ms ease-in-out, transform 75ms ease-in-out",
                      }}
                    >
                      <Plus className="w-4 h-4" />
                      <span translate="yes">Create Project</span>
                    </Button>
                  </div>

                  <div>
                    <p className="text-[var(--text-secondary)]/50" translate="yes">
                      Group your interviews into one research project. Each
                      project combines shared context.
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="all" className="w-full">
                <TabsList variant="underline" translate="yes">
                  <TabsTab value="all">
                    <span className="hidden sm:inline">All Projects</span>
                    <span className="sm:hidden">All</span>
                    <Badge variant="secondary">{allProjects.length}</Badge>
                  </TabsTab>
                  <TabsTab value="new">
                    New
                    <Badge variant="secondary">{newProjects.length}</Badge>
                  </TabsTab>
                  <TabsTab value="opened">
                    Opened
                    <Badge variant="secondary">{openedProjects.length}</Badge>
                  </TabsTab>
                  <TabsTab value="closed">
                    Closed
                    <Badge variant="secondary">{closedProjects.length}</Badge>
                  </TabsTab>
                </TabsList>

                <TabsPanel value="all" className="mt-2">
                  {renderProjectGrid(allProjects, { enableReorder: true })}
                </TabsPanel>

                <TabsPanel value="new" className="mt-2">
                  {newProjects.length > 0 ? (
                    renderProjectGrid(newProjects, { enableReorder: true })
                  ) : (
                    <div className="flex items-center justify-center min-h-[400px]">
                      <p className="text-[var(--text-secondary)]/50 text-center" translate="yes">
                        No new projects yet
                      </p>
                    </div>
                  )}
                </TabsPanel>

                <TabsPanel value="opened" className="mt-2">
                  {openedProjects.length > 0 ? (
                    renderProjectGrid(openedProjects, { enableReorder: true })
                  ) : (
                    <div className="flex items-center justify-center min-h-[400px]">
                      <p className="text-[var(--text-secondary)]/50 text-center" translate="yes">
                        No opened projects yet
                      </p>
                    </div>
                  )}
                </TabsPanel>

                <TabsPanel value="closed" className="mt-2">
                  {closedProjects.length > 0 ? (
                    renderProjectGrid(closedProjects, { enableReorder: true })
                  ) : (
                    <div className="flex items-center justify-center min-h-[400px]">
                      <p className="text-[var(--text-secondary)]/50 text-center" translate="yes">
                        No closed projects yet
                      </p>
                    </div>
                  )}
                </TabsPanel>
              </Tabs>

              {isAdmin && (
                <>
                  <div className="border-t border-transparent mt-12 pt-0" />
                  <div className="flex items-center gap-4 flex-wrap">
                    <h2 className="text-2xl font-semibold text-[var(--text)] whitespace-nowrap shrink-0" translate="yes">
                      User Projects
                    </h2>
                    <Input
                      placeholder="Search by email or project name..."
                      value={adminSearchValue}
                      onChange={(e) => onAdminSearchChange?.(e.target.value)}
                      className="text-sm bg-card h-9 w-64 cursor-pointer"
                    />
                  </div>
                  {adminError && (
                    <p className="text-sm text-destructive mt-2" translate="yes">
                      {adminError}
                    </p>
                  )}
                  <Tabs defaultValue="admin-all" className="w-full mb-12">
                    <TabsList variant="underline" translate="yes">
                      <TabsTab value="admin-all">
                        <span className="hidden sm:inline">All Projects</span>
                        <span className="sm:hidden">All</span>
                        <Badge variant="secondary">{adminAllProjects.length}</Badge>
                      </TabsTab>
                      <TabsTab value="admin-new">
                        New
                        <Badge variant="secondary">{adminNewProjects.length}</Badge>
                      </TabsTab>
                      <TabsTab value="admin-opened">
                        Opened
                        <Badge variant="secondary">{adminOpenedProjects.length}</Badge>
                      </TabsTab>
                      <TabsTab value="admin-closed">
                        Closed
                        <Badge variant="secondary">{adminClosedProjects.length}</Badge>
                      </TabsTab>
                    </TabsList>

                    <TabsPanel value="admin-all" className="mt-2">
                      {renderProjectGrid(adminAllProjects, {
                        loadingOverride: isAdminLoading,
                        enableReorder: false,
                      })}
                    </TabsPanel>

                    <TabsPanel value="admin-new" className="mt-2">
                      {adminNewProjects.length > 0 ? (
                        renderProjectGrid(adminNewProjects, {
                          loadingOverride: isAdminLoading,
                          enableReorder: false,
                        })
                      ) : (
                        <div className="flex items-center justify-center min-h-[200px]">
                          <p className="text-[var(--text-secondary)]/50 text-center" translate="yes">
                            No new projects yet
                          </p>
                        </div>
                      )}
                    </TabsPanel>

                    <TabsPanel value="admin-opened" className="mt-2">
                      {adminOpenedProjects.length > 0 ? (
                        renderProjectGrid(adminOpenedProjects, {
                          loadingOverride: isAdminLoading,
                          enableReorder: false,
                        })
                      ) : (
                        <div className="flex items-center justify-center min-h-[200px]">
                          <p className="text-[var(--text-secondary)]/50 text-center" translate="yes">
                            No opened projects yet
                          </p>
                        </div>
                      )}
                    </TabsPanel>

                    <TabsPanel value="admin-closed" className="mt-2">
                      {adminClosedProjects.length > 0 ? (
                        renderProjectGrid(adminClosedProjects, {
                          loadingOverride: isAdminLoading,
                          enableReorder: false,
                        })
                      ) : (
                        <div className="flex items-center justify-center min-h-[200px]">
                          <p className="text-[var(--text-secondary)]/50 text-center" translate="yes">
                            No closed projects yet
                          </p>
                        </div>
                      )}
                    </TabsPanel>
                  </Tabs>
                </>
              )}
            </div>
          </main>
        </div>
      </div>
      <ProgressiveBlur
        direction="bottom"
        blurLayers={6}
        blurIntensity={0.5}
        className="absolute bottom-0 left-0 right-0 z-10 h-8"
      />
    </div>
  );
}
